import { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';
import { logLogin } from '../services/api';

// Static Web Apps user info from /.auth/me
interface SWAUserInfo {
  clientPrincipal: {
    userId: string;
    userRoles: string[];
    claims: { typ: string; val: string }[];
    identityProvider: string;
    userDetails: string;
  } | null;
}

// Simplified user type for the app
interface AppUser {
  userId: string;
  name: string;
  username: string;
  roles: string[];
  groups: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AppUser | null;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AppUser | null>(null);
  const hasLoggedLogin = useRef(false);

  useEffect(() => {
    // Fetch user info from Static Web Apps auth endpoint
    async function fetchUserInfo() {
      try {
        const response = await fetch('/.auth/me');
        const data: SWAUserInfo = await response.json();

        if (data.clientPrincipal) {
          const principal = data.clientPrincipal;

          // Extract name from claims
          const nameClaim = principal.claims.find(c => c.typ === 'name');
          const emailClaim = principal.claims.find(c => c.typ === 'preferred_username' || c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress');
          const groupClaims = principal.claims.filter(c => c.typ === 'groups').map(c => c.val);

          setUser({
            userId: principal.userId,
            name: nameClaim?.val || principal.userDetails || 'User',
            username: emailClaim?.val || principal.userDetails || '',
            roles: principal.userRoles,
            groups: groupClaims,
          });

          // Log login event (once per session)
          if (!hasLoggedLogin.current) {
            hasLoggedLogin.current = true;
            logLogin().catch(err => console.warn('Failed to log login:', err));
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchUserInfo();
  }, []);

  const isAuthenticated = user !== null;

  const login = async () => {
    // Redirect to Azure AD login via Static Web Apps
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
  };

  const logout = () => {
    window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
  };

  const getAccessToken = async (): Promise<string> => {
    // For SWA built-in auth, the token is handled automatically via cookies
    // When calling the Function App directly (preview/staging), we need to
    // forward the client principal as a base64-encoded header
    return '';
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
